import React, { useState } from 'react';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import {
    Box,
    Button,
    TextField,
    Typography,
    Paper,
    Container,
    CircularProgress,
    TextareaAutosize
} from '@mui/material';
import { styled } from '@mui/material/styles';

// Configuración de axios
const api = axios.create({
    baseURL: 'https://dymesenergy.gst-panel.com'
});

// Estilos
const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(4),
    marginTop: theme.spacing(4),
    borderRadius: '12px',
    boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
    marginBottom: theme.spacing(3),
    '& .MuiOutlinedInput-root': {
        borderRadius: '8px',
    },
}));

const StyledTextarea = styled(TextareaAutosize)(({ theme }) => ({
    width: '100%',
    padding: theme.spacing(1.5),
    borderRadius: '8px',
    border: `1px solid ${theme.palette.mode === 'light' ? '#E0E3E7' : '#2D3843'}`,
    fontFamily: 'inherit',
    fontSize: '1rem',
    '&:focus': {
        borderColor: theme.palette.primary.main,
        outline: 'none',
    },
    marginBottom: theme.spacing(3),
}));

const StyledButton = styled(Button)(({ theme }) => ({
    padding: theme.spacing(1.5),
    borderRadius: '8px',
    fontWeight: 'bold',
    textTransform: 'none',
    fontSize: '16px',
}));

const TipoTechoForm = () => {
    const [nombre, setNombre] = useState('');
    const [descripcion, setDescripcion] = useState('');
    const [loading, setLoading] = useState(false);
    const { enqueueSnackbar } = useSnackbar();

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!nombre.trim()) {
            enqueueSnackbar('El nombre del tipo de techo es requerido', {
                variant: 'error',
                autoHideDuration: 3000
            });
            return;
        }

        if (!descripcion.trim()) {
            enqueueSnackbar('La descripción es requerida', {
                variant: 'error',
                autoHideDuration: 3000
            });
            return;
        }

        setLoading(true);

        try {
            const response = await api.post('/agregar/guardar_tipostecho.php', {
                Nombre: nombre,
                Descripcion: descripcion
            }, {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            });

            if (response.data.success) {
                enqueueSnackbar(`Tipo de techo "${nombre}" creado con éxito`, {
                    variant: 'success',
                    autoHideDuration: 5000,
                    anchorOrigin: {
                        vertical: 'top',
                        horizontal: 'right'
                    }
                });
                setNombre('');
                setDescripcion('');
            } else {
                throw new Error(response.data.message || 'Error al crear tipo de techo');
            }
        } catch (error) {
            console.error('Error:', error);
            let errorMessage = 'Ocurrió un problema al crear el tipo de techo';

            if (error.response) {
                errorMessage = error.response.data.message || errorMessage;
            } else if (error.request) {
                errorMessage = 'No se pudo conectar con el servidor';
            }

            enqueueSnackbar(errorMessage, {
                variant: 'error',
                autoHideDuration: 5000,
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'right'
                }
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container maxWidth="md">
            <StyledPaper elevation={3}>
                <Typography variant="h4" component="h1" gutterBottom sx={{
                    fontWeight: 'bold',
                    color: 'primary.main',
                    marginBottom: 3
                }}>
                    Agregar Tipo de Techo
                </Typography>

                <Box component="form" onSubmit={handleSubmit}>
                    <StyledTextField
                        fullWidth
                        label="Nombre del Tipo de Techo"
                        variant="outlined"
                        value={nombre}
                        onChange={(e) => setNombre(e.target.value)}
                        disabled={loading}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        helperText="Ejemplo: Techo de teja, losa plana, etc."
                    />

                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" gutterBottom sx={{ mb: 1 }}>
                            Descripción
                        </Typography>
                        <StyledTextarea
                            minRows={4}
                            placeholder="Ingrese la descripción detallada del tipo de techo"
                            value={descripcion}
                            onChange={(e) => setDescripcion(e.target.value)}
                            disabled={loading}
                        />
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <StyledButton
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={loading}
                            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                        >
                            {loading ? 'Guardando...' : 'Guardar Tipo de Techo'}
                        </StyledButton>
                    </Box>
                </Box>
            </StyledPaper>
        </Container>
    );
};

export default TipoTechoForm;